import React, { useState } from 'react';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  HelpCircle,
  Search,
  Users,
  Building2,
  Calendar,
  Clock,
  Coffee,
  FileText,
  Settings,
  Bell,
  MapPin,
  Shield,
  Camera,
  ArrowLeftRight,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  Play,
  UserPlus,
  Key,
  Gauge,
  Timer,
  BarChart3,
  Download,
  Archive,
  Eye,
  Lock,
  Smartphone
} from 'lucide-react';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Play,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      articles: [
        {
          title: 'Welcome to ClockIn Pro',
          content: `ClockIn Pro is a comprehensive time tracking and workforce management solution designed for modern workplaces. 

**Key Features:**
• **Progressive Web App (PWA)** - Install directly from your browser, no app store needed
• **Push Notifications** - Get real-time alerts for shifts, late clock-ins, and swaps
• **Geofenced Clock-in/out** - Employees can only clock in when within designated office locations
• **Break Management** - Track breakfast and lunch breaks with configurable durations
• **Roster Builder** - Weekly & monthly views with today highlighted and shift swap workflows
• **Overtime Tracking** - Monitor hours worked with configurable weekly/fortnightly periods
• **Facial Recognition** - Optional biometric verification for clock-in (browser-based)
• **Real-time Dashboard** - Monitor your workforce in real-time with analytics
• **AI Support** - 24/7 AI chatbot with ticket escalation for complex issues
• **Payroll Reminders** - Automated email alerts on your pay schedule
• **Comprehensive Reports** - Generate detailed time and attendance reports`
        },
        {
          title: 'First Steps as an Admin',
          content: `Follow these steps to get your company set up:

**1. Set Up Office Locations**
Navigate to **Offices** in the sidebar and add your workplace locations with geofence boundaries. Draw a polygon on the map to define where employees can clock in.

**2. Invite Employees**
Go to **Employees** and use the "Invite Employee" button. Enter their email address and they'll receive an invitation to join.

**3. Configure Settings**
Visit **Settings** to configure:
- Break durations
- Overtime thresholds
- Late clock-in alerts
- Facial recognition (optional)

**4. Create Rosters**
Use the **Roster** section to schedule employee shifts.

**5. Monitor Your Team**
Check the **Dashboard** for real-time status and analytics.`
        },
        {
          title: 'Understanding User Roles',
          content: `ClockIn Pro has three user roles:

**Company Admin**
- Full control over your company
- Manages employees, offices, and settings
- Views all reports and analytics
- Can also clock in/out (dual role)

**Manager**
- Subset of admin capabilities
- Manages team schedules and rosters
- Approves shift swaps
- Views team reports
- Can clock in/out as an employee (dual role)

**Employee**
- Clocks in/out at office locations
- Takes breaks
- Views personal time history
- Requests shift swaps`
        }
      ]
    },
    {
      id: 'mobile-pwa',
      title: 'Mobile App & PWA',
      icon: Smartphone,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
      articles: [
        {
          title: 'Installing the App on Your Phone',
          content: `ClockIn Pro is a Progressive Web App (PWA) - you can install it directly from your browser without visiting an app store.

**For iPhone/iPad:**
1. Open ClockIn Pro in Safari
2. Tap the **Share** button (square with arrow)
3. Scroll down and tap **"Add to Home Screen"**
4. Tap **"Add"** in the top right

**For Android:**
1. Open ClockIn Pro in Chrome
2. You'll see an **"Add to Home Screen"** banner at the bottom
3. Tap **"Install"** or use the browser menu (⋮) and select "Install app"

Once installed, ClockIn Pro will appear on your home screen like a regular app!`
        },
        {
          title: 'Push Notifications Setup',
          content: `Push notifications keep you updated about important events. Here's how to enable them:

**Enabling Push Notifications:**
1. Open ClockIn Pro on your device
2. Go to **Settings** (or look for the notification bell icon)
3. Click **"Enable Push Notifications"**
4. Allow notifications when your browser prompts you

**Types of Notifications:**
• **Shift Start Reminders** - Get notified before your shift begins
• **Late Clock-in Alerts** - Managers get notified when employees are late
• **Clock-Out Reminders** - Reminders for employees still clocked in
• **Shift Swap Requests** - Notifications when someone wants to swap with you

**Note:** Push notifications require an internet connection and browser permission.`
        },
        {
          title: 'Benefits of the PWA',
          content: `Why use the PWA instead of just the website?

**Key Benefits:**
• **Home Screen Access** - One tap to open, just like a native app
• **Push Notifications** - Stay updated even when the app is closed
• **Faster Loading** - Cached content loads instantly
• **Works Offline** - View your schedule without internet (clock-in requires connection)
• **No App Store Updates** - Always get the latest version automatically
• **Less Storage** - Uses less space than traditional apps

**Perfect For:**
• Employees clocking in from job sites
• Managers who need quick access to team status
• Anyone who wants instant access to their schedule`
        }
      ]
    },
    {
      id: 'employees',
      title: 'Employee Management',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      articles: [
        {
          title: 'Inviting New Employees',
          content: `To invite a new employee:

1. Go to **Employees** from the sidebar
2. Click **"Invite Employee"**
3. Enter the employee's email address
4. Click **"Send Invitation"**

The employee will receive an email with a registration link. Once they complete registration, they'll appear in your employee list.

**Note:** Invitation links expire after 7 days. You can resend invitations from the Pending Invitations tab.`
        },
        {
          title: 'Employee Information',
          content: `Each employee profile includes:

**Basic Information:**
- Name and Email
- Position/Job Title
- Payroll ID (for integration with payroll systems)

**Personal Details:**
- Date of Birth
- Address

**Status:**
- Active or Archived
- Manager status
- Face enrollment status (if facial recognition enabled)

To edit employee details, click the **Edit** button next to their name.`
        },
        {
          title: 'Promoting Employees to Managers',
          content: `Managers have additional responsibilities:

**To make an employee a manager:**
1. Find the employee in the list
2. Click **"Make Manager"** button

**Manager Capabilities:**
- View team dashboard
- Manage rosters
- Approve shift swap requests
- View team time entries
- Still clock in/out as employees (dual role)

**To remove manager status:**
Click **"Remove Manager"** on a manager's card.`
        },
        {
          title: 'Off-Site Clock-in Settings',
          content: `By default, employees must be within a geofenced office to clock in. You can allow specific employees to clock in from anywhere:

**To configure off-site access:**
1. Find the employee
2. Click the **"Off-site"** button
3. Toggle **"Allow Off-site Clock-in"** ON
4. Choose enforcement mode:
   - **Allow Mode**: Employee can clock in from anywhere
   - **Warn Mode**: Employee can clock in, but a warning is logged

This is useful for remote workers or employees visiting clients.`
        },
        {
          title: 'Archiving Employees',
          content: `When an employee leaves your company:

1. Click **"Archive"** on their profile
2. Confirm the action

**What happens when archived:**
- Employee can no longer log in
- Historical time records are preserved
- Employee is hidden from active lists
- Can be restored later if needed

**To restore an archived employee:**
1. Go to the **Archived** tab
2. Click **"Restore"** on the employee`
        }
      ]
    },
    {
      id: 'offices',
      title: 'Offices & Geofencing',
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      articles: [
        {
          title: 'Adding Office Locations',
          content: `To add a new office:

1. Go to **Offices** from the sidebar
2. Click **"Add Office"**
3. Enter the office name and address
4. Click on the map to create geofence points
5. Connect at least 3 points to form a polygon
6. Click **"Save Office"**

**Tips:**
- Make the geofence slightly larger than the building to account for GPS accuracy
- Include parking areas if employees clock in from their cars
- Test the boundaries with a few employees first`
        },
        {
          title: 'Understanding Geofencing',
          content: `Geofencing ensures employees can only clock in when physically present at work.

**How it works:**
1. Employee opens the app on their phone
2. App requests their GPS location
3. System checks if location is within any office geofence
4. If inside: Clock-in allowed
5. If outside: Clock-in blocked (unless off-site is enabled)

**GPS Accuracy:**
- Indoor GPS can be less accurate
- We recommend a buffer of 20-50 meters around buildings
- WiFi-assisted GPS is more accurate

**Troubleshooting:**
If employees have issues clocking in:
- Ensure location services are enabled on their device
- Check that the browser has location permission
- Verify the geofence covers the entire work area`
        },
        {
          title: 'Editing & Deleting Offices',
          content: `**To edit an office:**
1. Find the office in the list
2. Click the **Edit** icon
3. Modify the name, address, or geofence
4. Save changes

**To delete an office:**
1. Click the **Delete** icon
2. Confirm deletion

**Warning:** Deleting an office:
- Does NOT delete historical time entries
- Employees who were clocked in at that office remain clocked in
- The office name will show as "Unknown" in historical records`
        }
      ]
    },
    {
      id: 'roster',
      title: 'Roster & Scheduling',
      icon: Calendar,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      articles: [
        {
          title: 'Roster Views',
          content: `ClockIn Pro offers two roster views to help you manage schedules effectively:

**Weekly View:**
- Shows 7 days as columns with employees as rows
- Great for detailed day-by-day scheduling
- **Today's column is highlighted** with a "TODAY" badge
- Summary shows total shifts and hours per employee
- Daily notes row at the bottom for team-wide messages

**Monthly View:**
- Shows the entire month with dates as rows and employees as columns
- Perfect for seeing the big picture at a glance
- **Today's row is highlighted** with an indigo background and "TODAY" badge
- Click the "Monthly" tab to switch views
- Sticky headers keep employee names visible while scrolling

**Tips:**
- Use the "Today" button to quickly jump to the current week/month
- Click any cell to add or edit a shift
- The "TODAY" highlighting makes it easy to find the current day`
        },
        {
          title: 'Creating Shifts',
          content: `To create a shift:

1. Go to **Roster** from the sidebar
2. Select the week you want to schedule
3. Click **"Add Shift"** or click any empty cell
4. Select an employee
5. Set the date and start time
6. Optionally set an end time (leave blank for open-ended shifts)
7. Add a comment if needed
8. Click **"Save"**

**Shift Types:**
- **Fixed shifts**: Have both start and end times
- **Open-ended shifts**: Only have a start time (end determined by clock-out)

**Entry Types:**
- **Shift** - Regular work day with times
- **Holiday** - Public holiday
- **RDO** - Rostered Day Off
- **Sick** - Sick leave
- **Leave** - Annual leave
- **Day Off** - Standard day off`
        },
        {
          title: 'Shift Swaps',
          content: `Employees can request to swap shifts with coworkers.

**How shift swaps work:**
1. **Employee A** requests to swap their shift with **Employee B**
2. **Employee B** receives a push notification and can accept or decline
3. If accepted, a **Manager** reviews and approves/denies
4. If approved, shifts are automatically swapped in the roster

**Configuration Options** (in Settings):
- Enable/disable shift swaps
- "Swap with anyone" - allow swapping with any eligible employee
- Require manager approval
- Require coworker acceptance
- Maximum days ahead for swap requests

**Viewing Swap Requests:**
Managers can see pending swap requests in the **"Shift Swaps"** tab of the Roster section.`
        },
        {
          title: 'Daily Notes',
          content: `Add notes that the whole team can see for any day.

**Adding a Note:**
1. Look for the **"Daily Notes"** row at the bottom of the weekly roster
2. Click on any day's cell
3. Type your note
4. Click **Save**

**Use Cases:**
- Important announcements
- Public holiday notices
- Team meetings
- Special instructions for the day

**Who Can See Notes:**
- All admins and managers can add/edit notes
- All team members can view notes when viewing the roster`
        },
        {
          title: 'Late Clock-in Alerts',
          content: `Get notified when rostered employees don't clock in on time.

**How it works:**
1. System checks rostered shifts every 5 minutes (6 AM - 12 PM)
2. If an employee hasn't clocked in within the threshold (default: 15 minutes)
3. Notification sent to admins, managers, and optionally the late employee

**Configuration** (in Settings > Late Clock-in):
- Enable/disable alerts
- Threshold minutes (how late before alerting)
- Notify admin
- Notify managers
- Notify the late employee`
        }
      ]
    },
    {
      id: 'time-tracking',
      title: 'Time Tracking',
      icon: Clock,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
      articles: [
        {
          title: 'How Clock-in Works',
          content: `**For Employees:**
1. Open ClockIn Pro on their device
2. Allow location access
3. Press the **"Clock In"** button
4. If within geofence, clock-in is recorded
5. If facial recognition is enabled, verify identity

**What's Recorded:**
- Timestamp
- GPS location
- Office name
- Whether off-site (if applicable)

**Clock-out:**
Same process - press **"Clock Out"** when leaving.

**Admins can view:**
- Live status (who's clocked in)
- Time entry history
- Location verification`
        },
        {
          title: 'Viewing Time Entries',
          content: `**Dashboard:**
Shows real-time status of all employees (clocked in, on break, clocked out).

**Reports Section:**
Generate detailed time entry reports:
- Filter by date range
- Filter by employee
- Export to CSV

**Individual Employee:**
Click on an employee to see their personal time history.

**Time Entry Details:**
- Clock-in time
- Clock-out time
- Total hours
- Breaks taken
- Office location
- Off-site flag (if applicable)`
        },
        {
          title: 'Manual Time Adjustments',
          content: `If an employee forgot to clock in/out, admins can make adjustments:

1. Go to **Reports**
2. Find the time entry
3. Click **Edit**
4. Adjust times as needed
5. Add a note explaining the change

**Audit Trail:**
All manual adjustments are logged with:
- Who made the change
- Original values
- New values
- Timestamp
- Reason

**Best Practice:**
- Require employees to report issues same-day
- Add notes for all manual changes
- Review adjustments weekly`
        }
      ]
    },
    {
      id: 'breaks',
      title: 'Break Management',
      icon: Coffee,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      articles: [
        {
          title: 'Break Types',
          content: `ClockIn Pro supports two break types:

**Breakfast Break**
- Typically morning break
- Default duration: 30 minutes
- Usually taken 9-10 AM

**Lunch Break**
- Midday break
- Default duration: 60 minutes
- Usually taken 12-1 PM

**Break Workflow:**
1. Employee clicks **"Start Breakfast"** or **"Start Lunch"**
2. Timer starts counting
3. Employee clicks **"End Break"** when returning
4. Break duration is recorded and subtracted from work hours`
        },
        {
          title: 'Break Settings',
          content: `Configure break settings in **Settings > Break Configuration**:

**Duration Settings:**
- Breakfast duration (minutes)
- Lunch duration (minutes)

**Warning Settings:**
- Enable break warnings
- Warning threshold (minutes)

**How Warnings Work:**
If an employee exceeds the warning threshold (e.g., 60 minutes), they receive a notification reminding them to return to work.

**Break Time in Reports:**
- Breaks are tracked separately
- Total break time shown per day
- Net working hours = Clock-out - Clock-in - Break time`
        },
        {
          title: 'Break Analytics',
          content: `The Admin Dashboard shows break statistics:

**Break Time by Employee:**
- Average breakfast duration per employee
- Average lunch duration per employee
- Total break time
- Number of breaks taken

**Identifying Issues:**
- Employees consistently taking longer breaks
- Employees skipping breaks (compliance issue)
- Break patterns by day of week

**Best Practices:**
- Review break analytics weekly
- Address consistently long breaks early
- Ensure employees take required breaks (labor law compliance)`
        }
      ]
    },
    {
      id: 'overtime',
      title: 'Overtime Tracking',
      icon: TrendingUp,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      articles: [
        {
          title: 'Period Configuration',
          content: `Configure how overtime is calculated in **Settings > Overtime Tracking**:

**Period Type:**
- **Weekly**: Track hours per week
- **Fortnightly**: Track hours per fortnight (2 weeks)

**Standard Hours:**
- Weekly hours (default: 38)
- Fortnightly hours (default: 76)

**Week Start Day:**
Choose when your work week begins (Monday, Sunday, etc.)

**Fortnight Reference Date:**
For fortnightly tracking, set a reference date when your pay periods begin. This ensures period calculations align with your payroll cycle.`
        },
        {
          title: 'Overtime Alerts',
          content: `Get proactive alerts when employees approach overtime:

**Alert Settings:**
- Enable/disable overtime alerts
- Alert threshold (% of standard hours, default: 90%)
- Send email alerts to admins/managers
- Notify the employee themselves

**How It Works:**
1. System calculates hours worked in current period
2. Projects total hours based on current pace
3. If projected to exceed threshold, alerts are sent

**Alert Timing:**
Automated checks run at 12 PM and 5 PM daily.

**Alert Content:**
- Employee name
- Hours worked so far
- Projected overtime
- Days remaining in period`
        },
        {
          title: 'Period Progress Dashboard',
          content: `The Admin Dashboard shows period progress per employee:

**For Each Employee:**
- Hours worked / Standard hours
- Progress bar (color-coded)
- "On track" or "Near limit" badge
- Projected overtime (if applicable)
- Days remaining

**Color Coding:**
- 🟢 Green (0-75%): On track
- 🟡 Yellow (75-90%): Approaching limit
- 🟠 Orange (90-100%): Near overtime
- 🔴 Red (>100%): In overtime

**Employee View:**
Employees can see their own progress (if enabled in settings).
Admin toggle: "Show Period Progress to Employees"`
        }
      ]
    },
    {
      id: 'facial-recognition',
      title: 'Facial Recognition',
      icon: Camera,
      color: 'text-pink-600',
      bgColor: 'bg-pink-100',
      articles: [
        {
          title: 'Setting Up Facial Recognition',
          content: `Enable facial recognition in **Settings > Facial Recognition**:

**Enable/Disable:**
Toggle facial recognition on or off for your company.

**Enforcement Mode:**
- **Warn**: If face doesn't match, show warning but allow clock-in
- **Block**: If face doesn't match, prevent clock-in

**Liveness Check:**
Enable to prevent photos being used (detects real faces).

**Enrollment Required:**
If enabled, employees must enroll their face before they can clock in.`
        },
        {
          title: 'Employee Face Enrollment',
          content: `Employees must enroll their face before using facial recognition:

**Enrollment Process:**
1. Employee goes to their dashboard
2. Clicks "Enroll Face"
3. Allows camera access
4. Positions face in the camera frame
5. System captures and stores face data

**Best Practices for Enrollment:**
- Good lighting (face clearly visible)
- Face camera directly
- Remove glasses/hats if possible
- Neutral expression

**Re-enrollment:**
Employees can delete and re-enroll their face if needed.`
        },
        {
          title: 'Verification During Clock-in',
          content: `When facial recognition is enabled:

**Clock-in Flow:**
1. Employee clicks "Clock In"
2. Camera opens for verification
3. Employee positions face
4. System compares to enrolled face
5. If match: Clock-in proceeds
6. If no match: Based on enforcement mode (warn or block)

**Troubleshooting:**
- Ensure good lighting
- Remove sunglasses
- Face camera directly
- If issues persist, admin can disable for that employee

**Privacy:**
- Face data stored securely
- Only face descriptors stored (not photos)
- Data deleted when employee is removed`
        }
      ]
    },
    {
      id: 'reports',
      title: 'Reports & Analytics',
      icon: FileText,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      articles: [
        {
          title: 'Generating Reports',
          content: `Go to **Reports** from the sidebar:

**Available Report Types:**
- **Time Entries**: Clock-in/out records with hours worked
- **Break Summary**: Break durations by employee
- **Payroll Report**: Hours formatted for payroll integration

**Filters:**
- Date range (custom or presets)
- Employee (individual or all)
- Office location

**Export Options:**
- CSV (for spreadsheets/payroll)
- View on screen

**Tip:**
Export reports regularly to maintain backup records of your time tracking data.`
        },
        {
          title: 'Dashboard Analytics',
          content: `The Admin Dashboard provides real-time analytics:

**Summary Cards:**
- Total employees
- Currently clocked in
- On break
- Hours worked today

**Charts:**
- Net Hours by Day (line chart)
- Break Time by Employee
- Punctuality by Employee
- Period Progress by Employee

**Date Range:**
Use the date picker to adjust the analysis period:
- Last 7 days
- Last 14 days
- Last 30 days
- Custom range`
        },
        {
          title: 'Understanding Metrics',
          content: `**Net Hours:**
Total clock-in time minus break time.

**Punctuality %:**
(On-time arrivals / Scheduled shifts) × 100

**Overtime:**
Hours worked beyond standard period hours.

**Attendance Rate:**
(Days worked / Days scheduled) × 100

**Break Compliance:**
Whether breaks fall within configured durations.

**Period Progress:**
(Hours worked / Standard hours) × 100

**Projected Overtime:**
Based on current daily average × remaining days.`
        }
      ]
    },
    {
      id: 'settings',
      title: 'Company Settings',
      icon: Settings,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100',
      articles: [
        {
          title: 'Break Configuration',
          content: `**Settings > Break Configuration:**

**Durations:**
- Breakfast duration (default: 30 min)
- Lunch duration (default: 60 min)

**Warnings:**
- Enable break warnings
- Warning threshold (minutes)

Changes take effect immediately for all employees.`
        },
        {
          title: 'Overtime Settings',
          content: `**Settings > Overtime Tracking:**

**Period Type:**
- Weekly or Fortnightly

**Hours:**
- Standard weekly hours
- Standard fortnightly hours

**Timing:**
- Week start day
- Fortnight reference date

**Alerts:**
- Enable overtime alerts
- Alert threshold %
- Send email alerts
- Notify employees
- Show progress to employees`
        },
        {
          title: 'Shift Swap Settings',
          content: `**Settings > Shift Swap:**

**Options:**
- Enable/disable shift swaps
- Swap with any employee
- Require manager approval
- Require coworker acceptance
- Maximum days ahead for requests`
        },
        {
          title: 'Late Clock-in Settings',
          content: `**Settings > Late Clock-in:**

**Configuration:**
- Enable/disable alerts
- Threshold (minutes late)
- Notify admin
- Notify managers
- Notify the late employee

Alerts are sent when rostered employees haven't clocked in within the threshold after their scheduled start time. The late employee themselves can also receive a reminder notification.`
        },
        {
          title: 'Roster Notification Settings',
          content: `**Settings > Roster Notifications:**

Get notified when the roster for the upcoming week is incomplete.

**Configuration:**
- Enable/disable roster notifications
- Notification day (e.g., Wednesday, Thursday, Friday)
- Notify admins
- Notify managers

**How it works:**
1. System checks if the next week's roster has entries
2. On the selected day, if the roster is incomplete (no shifts scheduled), notifications are sent
3. Admins and/or managers receive an alert to complete the roster

This ensures you never forget to publish next week's schedule.`
        },
        {
          title: 'Clock-Out Reminder Settings',
          content: `**Settings > Clock-Out Reminder:**

Send reminders to employees who are still clocked in at a specific time.

**Configuration:**
- Enable/disable clock-out reminders
- Reminder time (e.g., 5:00 PM, 6:00 PM)
- Notify the employee
- Notify admins
- Notify managers

**How it works:**
1. At the configured time, the system checks for employees who are still clocked in
2. Notifications are sent to remind employees to clock out
3. Admins and/or managers can also be notified about employees still on the clock

This helps ensure employees don't forget to clock out at the end of their shift.`
        }
      ]
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: Bell,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      articles: [
        {
          title: 'Notification Types',
          content: `**System Notifications:**

**For Admins/Managers:**
- Late clock-in alerts
- Overtime warnings
- Shift swap requests
- Employee registration
- Incomplete roster reminders
- Clock-out reminders for active employees
- Payroll processing reminders

**For Employees:**
- Shift start reminders (push notification)
- Break duration warnings
- Shift swap responses
- Schedule changes
- Overtime notifications (if enabled)
- Late clock-in reminders (if enabled)
- Clock-out reminders (if enabled)

Notifications appear in the bell icon in the sidebar. Push notifications also appear on your device if enabled.`
        },
        {
          title: 'Push Notifications',
          content: `**Real-Time Alerts on Your Device:**

Push notifications deliver instant updates even when you're not actively using ClockIn Pro.

**Types of Push Notifications:**
• **Shift Start Reminders** - Get notified 30 minutes before your shift
• **Late Clock-in Alerts** - Managers see when employees haven't clocked in
• **Shift Swap Requests** - Know immediately when someone wants to swap
• **Clock-Out Reminders** - Reminder to clock out at end of day

**Enabling Push Notifications:**
1. Open ClockIn Pro in your browser
2. Look for the "Enable Notifications" button in Settings
3. Click **"Enable"** and allow in browser prompt
4. Choose which notification types you want

**Troubleshooting:**
- Make sure your browser allows notifications
- Check that ClockIn Pro isn't blocked in your device settings
- Push notifications require the PWA to be installed for best results
- Notifications may not work in private/incognito mode`
        },
        {
          title: 'Email Notifications',
          content: `**Email alerts are sent for:**

- Employee invitations
- Password reset requests
- Late clock-in alerts (if configured)
- Overtime projections (if configured)
- Shift swap notifications
- Incomplete roster alerts
- Clock-out reminders

**Email Settings:**
Email notifications are automatically configured for your company. All notification preferences can be managed in your Company Settings.`
        },
        {
          title: 'Roster Notifications',
          content: `**Reminder to Complete Rosters:**

Never forget to publish next week's schedule with automated roster notifications.

**How it works:**
1. Go to **Settings > Roster Notifications**
2. Enable roster notifications
3. Choose which day to be notified (e.g., Wednesday or Thursday)
4. Select who should receive notifications (admins, managers)

**When notifications are sent:**
- The system checks if any shifts are scheduled for the upcoming week
- If the roster is empty or incomplete, notifications are sent on the configured day
- This gives you time to complete and publish the schedule before the week starts

**Benefits:**
- Employees always have their schedule in advance
- Reduces last-minute scheduling stress
- Ensures compliance with schedule posting requirements`
        },
        {
          title: 'Clock-Out Reminders',
          content: `**End-of-Day Clock-Out Reminders:**

Ensure employees remember to clock out at the end of their workday.

**How it works:**
1. Go to **Settings > Clock-Out Reminder**
2. Enable clock-out reminders
3. Set the reminder time (e.g., 5:00 PM or 6:00 PM)
4. Choose who should receive notifications

**Who can be notified:**
- **Employees**: Get a reminder to clock out
- **Admins/Managers**: Get notified about employees still on the clock

**Benefits:**
- Reduces forgotten clock-outs
- Minimizes the need for manual time adjustments
- Helps maintain accurate time records
- Ensures compliance with working hours policies`
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertCircle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      articles: [
        {
          title: 'Location Issues',
          content: `**"Location unavailable" error:**

1. Check browser location permissions
2. Ensure GPS/location services enabled on device
3. Try refreshing the page
4. Move to an area with better GPS signal

**Employee outside geofence but at office:**

1. Check the geofence boundary in Offices
2. Expand the geofence to cover the full area
3. Consider enabling off-site clock-in temporarily`
        },
        {
          title: 'Clock-in Problems',
          content: `**Can't clock in:**

1. Verify employee is within geofence
2. Check if facial recognition is required
3. Ensure employee isn't already clocked in
4. Check if employee account is archived

**Accidental clock-out:**

Admin can:
1. Go to Reports
2. Find the entry
3. Edit the clock-out time
4. Add note explaining the correction`
        },
        {
          title: 'Account Issues',
          content: `**Forgot password:**
Use "Forgot Password" link on login page.

**Can't receive invitation:**
1. Check spam/junk folder
2. Verify email address is correct
3. Resend invitation from admin panel

**Account locked:**
Contact your company admin to restore access.`
        }
      ]
    },
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      icon: HelpCircle,
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
      articles: [
        {
          title: 'General Questions',
          content: `**Q: Can employees clock in from home?**
A: Only if their admin enables "Off-site clock-in" for them.

**Q: What happens if GPS is inaccurate?**
A: The geofence should be large enough to account for GPS drift (20-50m buffer recommended).

**Q: Can I track time across multiple offices?**
A: Yes, add all office locations and employees can clock in at any of them.

**Q: Is the data secure?**
A: Yes, all data is encrypted in transit and at rest. Facial data is stored as numerical descriptors, not actual photos.`
        },
        {
          title: 'Time & Overtime Questions',
          content: `**Q: How is overtime calculated?**
A: Hours worked above your configured standard hours (weekly or fortnightly).

**Q: Can I change the pay period mid-cycle?**
A: Yes, but it will affect current period calculations. Best to change at period boundaries.

**Q: Are breaks deducted from work hours?**
A: Yes, net hours = clock-in to clock-out minus break time.

**Q: What if an employee doesn't take breaks?**
A: You'll see this in break analytics. Consider requiring breaks for compliance.`
        },
        {
          title: 'Technical Questions',
          content: `**Q: Which browsers are supported?**
A: Chrome, Firefox, Safari, Edge (latest versions). Mobile browsers also supported.

**Q: Does the app work offline?**
A: No, internet connection is required for clock-in/out and all features.

**Q: Can I integrate with payroll software?**
A: Yes, export CSV reports in payroll-compatible format.

**Q: I have a question not covered here?**
A: Use the search bar above to find specific topics, or browse through the documentation sections. All features are fully documented in this Help Center.`
        }
      ]
    }
  ];

  // Filter articles based on search
  const filteredSections = helpSections.map(section => ({
    ...section,
    articles: section.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.articles.length > 0);

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Manrope'] text-slate-900">
                Help Center
              </h1>
              <p className="text-slate-500">
                Learn how to use ClockIn Pro effectively
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 text-lg border-slate-200 focus:border-[hsl(243,65%,20%)] focus:ring-[hsl(243,65%,20%)]"
              data-testid="help-search"
            />
          </div>
        </div>

        {/* Quick Links */}
        {!searchQuery && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Quick Links
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { title: 'Add Employee', icon: UserPlus, color: 'bg-blue-100 text-blue-600', section: 'employees' },
                { title: 'Create Shift', icon: Calendar, color: 'bg-indigo-100 text-indigo-600', section: 'roster' },
                { title: 'Add Office', icon: Building2, color: 'bg-purple-100 text-purple-600', section: 'offices' },
                { title: 'Configure Overtime', icon: TrendingUp, color: 'bg-red-100 text-red-600', section: 'overtime' },
              ].map((link) => (
                <button
                  key={link.title}
                  onClick={() => {
                    setExpandedSections([link.section]);
                    document.getElementById(link.section)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-[hsl(243,65%,20%)] hover:shadow-md transition-all"
                >
                  <div className={`w-10 h-10 rounded-lg ${link.color} flex items-center justify-center`}>
                    <link.icon className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-slate-700">{link.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Help Sections */}
        <div className="space-y-4">
          {filteredSections.map((section) => (
            <Card 
              key={section.id} 
              id={section.id}
              className="border-0 shadow-lg overflow-hidden"
            >
              <Collapsible
                open={expandedSections.includes(section.id)}
                onOpenChange={() => toggleSection(section.id)}
              >
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${section.bgColor} flex items-center justify-center`}>
                          <section.icon className={`w-5 h-5 ${section.color}`} />
                        </div>
                        <div className="text-left">
                          <CardTitle className="font-['Manrope']">{section.title}</CardTitle>
                          <CardDescription>{section.articles.length} articles</CardDescription>
                        </div>
                      </div>
                      {expandedSections.includes(section.id) ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {section.articles.map((article, index) => (
                        <div 
                          key={index}
                          className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                        >
                          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {article.title}
                          </h3>
                          <div className="prose prose-sm prose-slate max-w-none">
                            {article.content.split('\n\n').map((paragraph, pIndex) => (
                              <div key={pIndex} className="mb-3">
                                {paragraph.split('\n').map((line, lIndex) => {
                                  // Handle bold text
                                  const parts = line.split(/\*\*(.*?)\*\*/g);
                                  const formattedLine = parts.map((part, i) => 
                                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                                  );
                                  
                                  // Handle bullet points
                                  if (line.startsWith('•') || line.startsWith('-')) {
                                    return (
                                      <div key={lIndex} className="flex gap-2 ml-4 text-slate-600">
                                        <span className="text-slate-400">•</span>
                                        <span>{formattedLine}</span>
                                      </div>
                                    );
                                  }
                                  
                                  // Handle numbered lists
                                  if (/^\d+\./.test(line)) {
                                    return (
                                      <div key={lIndex} className="ml-4 text-slate-600">
                                        {formattedLine}
                                      </div>
                                    );
                                  }
                                  
                                  // Handle Q&A format
                                  if (line.startsWith('Q:') || line.startsWith('A:')) {
                                    return (
                                      <div key={lIndex} className={`${line.startsWith('Q:') ? 'font-medium text-slate-800' : 'text-slate-600 ml-4'}`}>
                                        {formattedLine}
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <p key={lIndex} className="text-slate-600">
                                      {formattedLine}
                                    </p>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>

        {/* No results */}
        {searchQuery && filteredSections.length === 0 && (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12 text-center">
              <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No articles found</h3>
              <p className="text-slate-500">
                Try searching with different keywords or browse the sections above.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Feedback Card */}
        <Card className="border-0 shadow-lg mt-8 bg-gradient-to-r from-[hsl(243,65%,20%)] to-[hsl(243,65%,30%)] text-white">
          <CardContent className="py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Can't find what you're looking for?</h3>
                  <p className="text-white/80">Browse through the sections above or use search to find specific topics</p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white border-white/30 px-4 py-2">
                Self-service documentation
              </Badge>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default HelpCenter;
